// useFairPriceOutputs — Fair Price Dashboard data source.
//
// DASH-HARDEN-2 (2026-05-20): repointed from public.fair_price_outputs
// to public.fair_price_predictions. Two FPE writers existed and the
// dashboard was bound to the LEGACY one (P21 / compute_fair_prices_for_window)
// which emits 0.5000 BTTS/OU defaults whenever league_priors is absent.
// The newer 1B.10.2 engine (scripts/run-fair-price-engine.ts) writes
// to fair_price_predictions with an honest sample-size gate (n>=20)
// and refuses to fabricate when inputs are thin. That's the engine
// today's OP-RESULTS chain feeds.
//
// Net effect of the rewire:
//   * Dashboard renders ONLY rows the 1B.10.2 engine has honestly
//     priced (prediction_status='ready' AND expires_at > now()).
//   * Until each league accrues n>=20 settled matches, the dashboard
//     for those leagues is HONESTLY EMPTY rather than showing the
//     0.5000 placeholders. Empty is better than fabricated.
//
// Boundaries (unchanged):
//   - Authenticated SELECT only; RLS grants come from the table's own
//     authenticated-read policy.
//   - No service-role, no edge function, no RPC.
//   - kickoff_at gates the window; we never cap below 100 rows.
//
// db/types.ts does not yet include fair_price_predictions or
// clv_consensus; we cast through `as unknown as ...`. TODO: regen.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface FairPriceRow {
  readonly fixture_id: string;
  readonly home_team: string;
  readonly away_team: string;
  readonly competition: string;
  readonly kickoff_at: string;
  readonly market: string;
  readonly selection: string;
  readonly fair_prob: number;
  readonly fair_odds: number;
  readonly bookmaker_prob: number | null;
  readonly bookmaker_odds: number | null;
  readonly edge_pct: number | null;
  readonly computed_at: string;
  readonly model_inputs: Record<string, unknown>;
}

export interface FairPriceFixture {
  readonly fixture_id: string;
  readonly home_team: string;
  readonly away_team: string;
  readonly competition: string;
  readonly kickoff_at: string;
  readonly headline_row: FairPriceRow;
  readonly all_rows: readonly FairPriceRow[];
}

export interface FairPriceOutputsResult {
  readonly rows: readonly FairPriceRow[];
  readonly fixtures: readonly FairPriceFixture[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
}

export interface UseFairPriceOutputsArgs {
  readonly horizonHours?: number;
}

const DEFAULT_HORIZON_HOURS = 168; // 7 days

interface FixtureJoinRow {
  fixture_id: string;
  kickoff_at: string;
  home: { canonical_name: string | null } | null;
  away: { canonical_name: string | null } | null;
  competition: { canonical_name: string | null } | null;
}

// public.fair_price_outputs — compute_fair_prices_for_window write target.
// market/selection already split; fair_prob/fair_odds numeric; computed_at ts.
interface FpoRow {
  fixture_id: string;
  market: string;
  selection: string;
  fair_prob: number | string;
  fair_odds: number | string;
  computed_at: string;
}

interface ClvRow {
  fixture_id: string;
  market: string;
  selection: string;
  weighted_prob: number | string | null;
  pinnacle_odds: number | string | null;
  betexplorer_odds: number | string | null;
  oddsportal_odds: number | string | null;
}

// canonical_code → (market, selection) split. The 1B.10.2 engine's
// SUPPORTED_CANONICAL_CODES currently covers only the 1X2 triplet;
// when the engine extends to BTTS / OU / handicap, add their codes
// here. An unknown code falls back to (canonical_code, "") so the
// row is still rendered honestly rather than silently dropped.
const CANONICAL_CODE_SPLIT: ReadonlyMap<string, { market: string; selection: string }> =
  new Map([
    ["match_winner_home", { market: "match_winner", selection: "home" }],
    ["match_winner_draw", { market: "match_winner", selection: "draw" }],
    ["match_winner_away", { market: "match_winner", selection: "away" }],
  ]);

function splitCanonicalCode(code: string): { market: string; selection: string } {
  return CANONICAL_CODE_SPLIT.get(code) ?? { market: code, selection: "" };
}

function toN(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function clvKey(fixtureId: string, market: string, selection: string): string {
  return `${fixtureId}::${market}::${selection}`;
}

// Model (fair_price_outputs) → consensus (clv_consensus) taxonomy bridge.
// The two writers use different market vocabularies, so a naive
// (fixture, market, selection) join never matches and every edge reads
// null. This translates a model row's market/selection into the consensus
// table's encoding so the lookup lands. Returns null when no consensus
// counterpart exists (e.g. BTTS) — that row stays honestly edge-less.
//
//   match_winner / home,draw,away   → 1x2 / home,draw,away
//   ou / over,under                 → total_goals_2_5 / over,under   (lineless "ou" is the 2.5 line, per MK-2)
//   asian_handicap / home_minus_0_5 → asian_handicap_minus_0_5 / home (line moves market-side, selection = side)
//   btts / yes,no                   → (none) — consensus does not emit BTTS yet
export function toConsensusKey(
  market: string,
  selection: string,
): { market: string; selection: string } | null {
  if (market === "match_winner") {
    return selection === "home" || selection === "draw" || selection === "away"
      ? { market: "1x2", selection }
      : null;
  }
  if (market === "ou") {
    return selection === "over" || selection === "under"
      ? { market: "total_goals_2_5", selection }
      : null;
  }
  if (market === "asian_handicap") {
    const m = /^(home|away)_(minus|plus)_(.+)$/.exec(selection);
    if (!m) return null;
    return { market: `asian_handicap_${m[2]}_${m[3]}`, selection: m[1]! };
  }
  return null;
}

function pickBookmakerOdds(c: ClvRow): number | null {
  // Prefer pinnacle, then betexplorer, then oddsportal — first non-null.
  return (
    toN(c.pinnacle_odds) ?? toN(c.betexplorer_odds) ?? toN(c.oddsportal_odds)
  );
}

function rowEdgePct(fairProb: number, bookmakerProb: number | null): number | null {
  if (bookmakerProb === null || bookmakerProb <= 0) return null;
  return ((fairProb - bookmakerProb) / bookmakerProb) * 100;
}

export function groupByFixture(rows: readonly FairPriceRow[]): readonly FairPriceFixture[] {
  const byId = new Map<string, FairPriceRow[]>();
  for (const r of rows) {
    const list = byId.get(r.fixture_id);
    if (list) list.push(r);
    else byId.set(r.fixture_id, [r]);
  }
  const fixtures: FairPriceFixture[] = [];
  for (const [fixture_id, list] of byId) {
    if (list.length === 0) continue;
    const sorted = [...list].sort((a, b) => {
      // Highest |edge_pct| first; rows without edge sort last.
      const ae = a.edge_pct;
      const be = b.edge_pct;
      if (ae === null && be === null) return b.fair_prob - a.fair_prob;
      if (ae === null) return 1;
      if (be === null) return -1;
      return be - ae;
    });
    const headline = sorted[0]!;
    fixtures.push({
      fixture_id,
      home_team: headline.home_team,
      away_team: headline.away_team,
      competition: headline.competition,
      kickoff_at: headline.kickoff_at,
      headline_row: headline,
      all_rows: list,
    });
  }
  // Default ordering: fixture by best edge desc, then by kickoff asc.
  fixtures.sort((a, b) => {
    const ae = a.headline_row.edge_pct;
    const be = b.headline_row.edge_pct;
    if (ae !== null && be !== null && ae !== be) return be - ae;
    if (ae !== null && be === null) return -1;
    if (ae === null && be !== null) return 1;
    return a.kickoff_at.localeCompare(b.kickoff_at);
  });
  return fixtures;
}

export function useFairPriceOutputs(
  args: UseFairPriceOutputsArgs = {},
): FairPriceOutputsResult {
  const horizonHours = args.horizonHours ?? DEFAULT_HORIZON_HOURS;
  const supabase = useMemo(() => getBrowserClient(), []);
  const [rows, setRows] = useState<readonly FairPriceRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const horizon = new Date(now.getTime() + horizonHours * 3600 * 1000);

      // Step 1: Fetch upcoming fixtures in the window (with labels).
      // Then fetch fair prices only for those fixture IDs.
      // This avoids the 1,000-row PostgREST cap on fair_price_outputs
      // (1,921 rows is_current across 179 fixtures, most already past).
      const fixturesResp = await supabase
        .from("canonical_fixtures")
        .select(
          "fixture_id, kickoff_at, home:teams!canonical_fixtures_home_team_id_fkey(canonical_name), away:teams!canonical_fixtures_away_team_id_fkey(canonical_name), competition:competitions!canonical_fixtures_competition_id_fkey(canonical_name)",
        )
        .eq("is_active", true)
        .gte("kickoff_at", now.toISOString())
        .lte("kickoff_at", horizon.toISOString())
        .order("kickoff_at", { ascending: true })
        .limit(500);

      if (fixturesResp.error) {
        setError(fixturesResp.error.message);
        setRows([]);
        return;
      }
      const fixtures = (fixturesResp.data ?? []) as unknown as FixtureJoinRow[];
      const fixtureMap = new Map<string, FixtureJoinRow>();
      for (const f of fixtures) fixtureMap.set(f.fixture_id, f);
      const allFixtureIds = fixtures.map((f) => f.fixture_id);

      if (allFixtureIds.length === 0) {
        setRows([]);
        return;
      }

      // Step 2: Fair-price rows for those upcoming fixtures.
      // Batch the .in() in chunks of 100 to avoid PostgREST URL limits.
      const CHUNK = 100;
      const fpoRaw: FpoRow[] = [];
      for (let i = 0; i < allFixtureIds.length; i += CHUNK) {
        const chunk = allFixtureIds.slice(i, i + CHUNK);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fppResp = await (supabase as any)
          .from("fair_price_outputs")
          .select("fixture_id, market, selection, fair_prob, fair_odds, computed_at")
          .eq("is_current", true)
          .in("fixture_id", chunk);
        if (!fppResp.error && fppResp.data) {
          fpoRaw.push(...(fppResp.data as FpoRow[]));
        }
      }

      // Dedup: keep newest computed_at per (fixture, market, selection)
      const fpoLatest = new Map<string, FpoRow>();
      for (const r of fpoRaw) {
        const k = `${r.fixture_id}::${r.market}::${r.selection}`;
        const prev = fpoLatest.get(k);
        if (!prev || String(r.computed_at) > String(prev.computed_at)) fpoLatest.set(k, r);
      }
      const fppRows = Array.from(fpoLatest.values());
      const fixtureIds = Array.from(new Set(fppRows.map((r) => r.fixture_id)));

      // Step 3: bookmaker consensus for the same fixtures (left join).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clvResp = await (supabase as any)
        .from("clv_consensus")
        .select("fixture_id, market, selection, weighted_prob, pinnacle_odds, betexplorer_odds, oddsportal_odds")
        .in("fixture_id", fixtureIds);

      const clvRows = ((clvResp?.data ?? []) as ClvRow[]) ?? [];
      const clvMap = new Map<string, ClvRow>();
      for (const c of clvRows) {
        clvMap.set(clvKey(c.fixture_id, c.market, c.selection), c);
      }

      const next: FairPriceRow[] = [];
      for (const fp of fppRows) {
        const fix = fixtureMap.get(fp.fixture_id);
        if (!fix) continue;
        const home = fix.home?.canonical_name ?? "Home";
        const away = fix.away?.canonical_name ?? "Away";
        const competition = fix.competition?.canonical_name ?? "—";
        const market = fp.market;
        const selection = fp.selection;
        const fairProb = toN(fp.fair_prob) ?? 0;
        const fairOdds = toN(fp.fair_odds) ?? 0;
        const ck = toConsensusKey(market, selection);
        const clv = ck
          ? clvMap.get(clvKey(fp.fixture_id, ck.market, ck.selection)) ?? null
          : null;
        const bookmakerProb = clv ? toN(clv.weighted_prob) : null;
        const bookmakerOdds = clv ? pickBookmakerOdds(clv) : null;
        const edge = rowEdgePct(fairProb, bookmakerProb);
        next.push({
          fixture_id: fp.fixture_id,
          home_team: home,
          away_team: away,
          competition,
          kickoff_at: fix.kickoff_at,
          market,
          selection,
          fair_prob: fairProb,
          fair_odds: fairOdds,
          bookmaker_prob: bookmakerProb,
          bookmaker_odds: bookmakerOdds,
          edge_pct: edge,
          computed_at: String(fp.computed_at),
          model_inputs: {},
        });
      }
      setRows(next);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, horizonHours]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const fixtures = useMemo(() => groupByFixture(rows), [rows]);

  return useMemo(
    () => ({ rows, fixtures, loading, error, refetch }),
    [rows, fixtures, loading, error, refetch],
  );
}

// Manual sanity check (live database, executed 2026-05-20 during
// DASH-HARDEN-2 verification): with horizonHours = 168 and the new
// table binding, this hook returns 0 rows. That's the honest state:
// the 1B.10.2 engine has not written any predictions yet (sample-size
// gate MIN_BASELINE_SAMPLE_SIZE=20 isn't met for any league). The
// dashboard now renders empty rather than the prior 0.5000 placeholders.
